import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { RootState } from '../../../reducers';
import { sendEmail } from '../../../services/emailService';
import { wrapWithTheme } from '../../email/templates';
import { USER_PACKAGE } from '../../plan/userPackage';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { cn } from '../../../lib/utils';

interface FormData {
  name: string;
  email: string;
  category: string;
  message: string;
}

interface SupportFormProps {
  formSource?: string;
}

const SupportForm: React.FC<SupportFormProps> = ({ formSource }) => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.settings);
  const { subscription, usage, marketing } = useSelector(
    (state: RootState) => state.plan,
  );
  const { selectedTeam } = useSelector((state: RootState) => state.team);

  // Determine if user is on a paid plan
  const { package: userPackage } = subscription || {};
  const isPaidUser =
    userPackage &&
    userPackage !== USER_PACKAGE.FREE &&
    userPackage !== USER_PACKAGE.FREE_LOGGEDIN;

  // Define categories based on user type
  const supportCategories = isPaidUser
    ? [
        { value: 'Reporting problem', label: 'Reporting problem' },
        { value: 'Product Request', label: 'Product Request' },
        { value: 'Technical Support', label: 'Technical Support' },
        { value: 'Billing Issues', label: 'Billing Issues' },
        { value: 'Cancellations', label: 'Cancellations' },
      ]
    : [
        { value: 'Reporting problem', label: 'Reporting problem' },
        { value: 'Product Request', label: 'Product Request' },
        { value: 'Technical Support', label: 'Technical Support' },
        { value: 'Sales Inquiry', label: 'Sales Inquiry' },
        { value: 'Account Issues', label: 'Account Issues' },
      ];

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    name: user?.name || '',
    email: user?.email || '',
    category: '',
    message: '',
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [keywordNotices, setKeywordNotices] = useState<any[]>([]);

  // Define keyword-based notices (scalable for future additions)
  const keywordNoticeConfig = [
    {
      keywords: ['refund', 'refunds', 'money back', 'return money'],
      notice:
        'Help us process this faster - what specific issue led to your refund request?',
      type: 'info' as const,
    },
    {
      keywords: [
        'billing',
        'charged',
        'payment',
        'credit card',
        'invoice',
        'subscription fee',
      ],
      notice:
        'Check Account Settings > Billing first, or include your invoice number.',
      type: 'info' as const,
    },
    {
      keywords: [
        'cancel',
        'unsubscribe',
        'stop subscription',
        'end subscription',
        'dont want',
      ],
      notice:
        'You can cancel your subscription anytime in Account Settings > Billing.',
      type: 'warning' as const,
      hasAction: true,
      actionText: 'Go to Billing',
      actionHandler: () => navigate('/settings/billing'),
    },
  ];

  const validateForm = () => {
    const newErrors: Partial<FormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.category.trim()) {
      newErrors.category = 'Category is required';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);
      try {
        const emailContent = `
<h2>Support Request from User</h2>

<table style="width: 100%; border-collapse: collapse;">
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Form Source:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          formSource || 'Contact Support Form'
        }</td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Name:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          formData.name
        }</td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Email:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          formData.email
        }</td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Category:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          formData.category
        }</td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Message:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${formData.message.replace(
          /\n/g,
          '<br />',
        )}</td>
</tr>
${
  marketing?.intent
    ? `
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Initial Intent:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${marketing.intent}</td>
</tr>
`
    : ''
}

<tr>
<td colspan="2" style="padding: 24px 0 12px 0;"><strong>User Information</strong></td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Current Plan:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          subscription?.package || 'No subscription'
        } (${subscription?.term || 'N/A'})</td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Current Price:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">$${
          subscription?.price || 0
        }</td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Subscription Since:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          subscription?.currentPeriodStartedAt
            ? new Date(subscription.currentPeriodStartedAt).toLocaleDateString()
            : 'N/A'
        }</td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Stripe ID:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          subscription?.providerId || 'N/A'
        }</td>
</tr>

<tr>
<td colspan="2" style="padding: 24px 0 12px 0;"><strong>Usage Statistics</strong></td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Total Uploads:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          usage?.uploadCount || 0
        }</td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>AI Interactions:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          usage?.askAICount || 0
        }</td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Video AI Interactions:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          usage?.videoAskAICount || 0
        }</td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Transcriptions:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          usage?.transcriptionCount || 0
        }</td>
</tr>

<tr>
<td colspan="2" style="padding: 24px 0 12px 0;"><strong>Team Information</strong></td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Team Name:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          selectedTeam?.name || 'No team'
        }</td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Team Members:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          selectedTeam?.teamSpaceMembers?.length || 0
        }</td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Team Created:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          selectedTeam?.createdAt
            ? new Date(selectedTeam.createdAt).toLocaleDateString()
            : 'N/A'
        }</td>
</tr>

<tr>
<td colspan="2" style="padding: 24px 0 12px 0;"><strong>System Information</strong></td>
</tr>
<tr>
<td style="padding: 12px 0;"><strong>OS:</strong></td>
<td style="padding: 12px 0;">${window.navigator.platform}</td>
</tr>
<tr>
<td style="padding: 12px 0;"><strong>Browser:</strong></td>
<td style="padding: 12px 0;">${window.navigator.userAgent}</td>
</tr>
</table>
`;

        await sendEmail({
          template: 'genericEmail',
          toEmail: 'support@screenapp.io',
          data: {
            content: wrapWithTheme(emailContent),
            subject: `[${formData.category}] [${
              subscription?.package || 'FREE'
            }] Support Request from ${formData.name}`,
          },
        });

        // Send confirmation email to the user
        const confirmationEmailContent = `
<h2>Thank you for contacting ScreenApp Support!</h2>

<p>We have received your support request and will get back to you within 1 working day.</p>

<hr style="margin: 24px 0; border: none; border-top: 1px solid #eee;" />

<h3>Your Support Request Details:</h3>
<table style="width: 100%; border-collapse: collapse;">
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Category:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          formData.category
        }</td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Name:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          formData.name
        }</td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Email:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
          formData.email
        }</td>
</tr>
<tr>
<td style="padding: 12px 0; border-bottom: 1px solid #eee; vertical-align: top;"><strong>Message:</strong></td>
<td style="padding: 12px 0; border-bottom: 1px solid #eee;">${formData.message.replace(
          /\n/g,
          '<br />',
        )}</td>
</tr>
</table>

<hr style="margin: 24px 0; border: none; border-top: 1px solid #eee;" />

<p style="color: #666; font-size: 14px;">
If you have any additional questions, please reply to this email or visit our
<a href="https://screenapp.io" style="color: #007bff;">website</a>.
</p>

<p style="color: #666; font-size: 14px;">
Best regards,<br />
The ScreenApp Support Team
</p>
`;

        await sendEmail({
          template: 'genericEmail',
          toEmail: formData.email,
          data: {
            content: wrapWithTheme(confirmationEmailContent),
            subject: `Support Request Received - ${formData.category}`,
          },
        });

        setIsSuccess(true);
        // Reset form after successful submission
        setFormData({
          name: '',
          email: '',
          category: '',
          message: '',
        });
      } catch (error) {
        console.error('Failed to submit form:', error);
        toast.error('Failed to submit form. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }

    // Check for keyword notices when message field changes
    if (name === 'message') {
      const detectedNotices: any[] = [];
      const messageLower = value.toLowerCase();

      keywordNoticeConfig.forEach((config) => {
        const hasKeyword = config.keywords.some((keyword) =>
          messageLower.includes(keyword.toLowerCase()),
        );
        if (hasKeyword) {
          detectedNotices.push(config);
        }
      });

      setKeywordNotices(detectedNotices);
    }
  };

  if (isSuccess) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="p-4 rounded-2xl">
          <div className="text-center p-8">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h4 className="text-2xl font-semibold text-green-500 mb-2">
              Thank You for Contacting Support!
            </h4>
            <p className="text-muted-foreground">
              We have received your message and will get back to you within 1
              working day.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="p-4 rounded-2xl">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium">
                Full Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={cn(
                  'rounded-full bg-background',
                  errors.name && 'border-red-500',
                )}
                placeholder="Enter your full name"
              />
              {errors.name && (
                <p className="text-xs text-red-500 mt-1">{errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className={cn(
                  'rounded-full bg-background',
                  errors.email && 'border-red-500',
                )}
                placeholder="Enter your email"
              />
              {errors.email && (
                <p className="text-xs text-red-500 mt-1">{errors.email}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="category" className="text-sm font-medium">
              Category <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.category}
              onValueChange={(value) => {
                setFormData((prev) => ({ ...prev, category: value }));
                if (errors.category) {
                  setErrors((prev) => ({ ...prev, category: undefined }));
                }
              }}
            >
              <SelectTrigger
                id="category"
                className={cn(
                  'rounded-full bg-background',
                  errors.category && 'border-red-500',
                )}
              >
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {supportCategories.map((category) => (
                  <SelectItem key={category.value} value={category.value}>
                    {category.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.category && (
              <p className="text-xs text-red-500 mt-1">{errors.category}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="message" className="text-sm font-medium">
              Message <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="message"
              name="message"
              rows={4}
              value={formData.message}
              onChange={handleChange}
              className={cn(
                'bg-background resize-none',
                errors.message && 'border-red-500',
              )}
              placeholder="Describe your issue or question"
            />
            {errors.message && (
              <p className="text-xs text-red-500 mt-1">{errors.message}</p>
            )}
            {keywordNotices.length > 0 && (
              <div className="space-y-2">
                {keywordNotices.map((config, index) => (
                  <Alert
                    key={index}
                    className={cn(
                      config.type === 'warning' &&
                        'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/10',
                      config.type === 'info' &&
                        'border-blue-500 bg-blue-50 dark:bg-blue-900/10',
                    )}
                  >
                    <AlertDescription className="flex items-center justify-between">
                      <span className="text-sm">{config.notice}</span>
                      {config.hasAction && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={config.actionHandler}
                          className="ml-2"
                        >
                          {config.actionText}
                        </Button>
                      )}
                    </AlertDescription>
                  </Alert>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="rounded-full px-6"
            >
              {isSubmitting ? (
                <>Sending...</>
              ) : (
                <>
                  Send Message
                  <Send className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SupportForm;
